<template>
    <div>
        <b-button :variant="btn_variant" :size="btn_size" :disabled="check_disable || is_request_required_files || is_submitting"
            @click="checkSubmitRequiredFiles"
            >
            <div class="d-flex align-items-center">
                <slot></slot>
                <b-spinner v-show="is_request_required_files || is_submitting" small class="ml-2" />
            </div>
        </b-button>

        <b-modal ref="modal-required-attachments-list" :title="$t('첨부 누락 문항 확인 알림')"
            size="xl" centered scrollable
            :no-close-on-esc="is_submitting"
            :no-close-on-backdrop="is_submitting"
            >
            <div class="text-center bg-white border border-gray f-105 rounded p-3 mb-3">
                <div v-show="$i18n.locale === 'ko'">
                    <!-- 아래 목록은 <b-badge variant="danger">첨부필수</b-badge> 문항 중 첨부가 업로드 되지 않은 문항입니다. <u class="fw-600"> 최종 제출하기 전에 다시 한번 확인해 주세요. </u>
                    <br> 현재 상태로 제출을 원하실 경우, 하단 <b>"최종 제출하기"</b>를 클릭해주세요. -->
                    답변 완료 버튼이 체크되지 않은 영역이나, 첨부 필수 문항에 증빙을 첨부하지 않은 항목을 확인하기 위한 확인 창입니다. 최종 제출 전, 해당 영역 및 문항을 다시 한 번 점검해 주세요. 
                    현재 상태로 제출을 원하시면, 하단의 <b>"최종 제출하기"</b> 버튼을 클릭해 주세요.

                </div>
                <div v-show="$i18n.locale === 'en'">
                    <!-- The following items are marked as <b-badge variant="danger">{{ $t('첨부필수') }}</b-badge> but have not been uploaded yet. 
                    <u class="fw-600">Please double-check before final submission.</u>
                    <br> If you wish to proceed with the current status, please click <b>"{{ $t('최종 제출하기') }}"</b> below. -->
                    This confirmation window is to check areas where the 'Completed Response' button has not been checked, or items where supporting documents were not attached for mandatory fields.
                    Please review these areas and items once again before final submission. 
                    If you wish to submit in the current state, click the <b>"{{ $t('최종 제출하기') }}"</b> button below.

                </div>
                <div v-show="$i18n.locale === 'ca'">
                    <!-- 以下为 <b-badge variant="danger">{{ $t('첨부필수') }}</b-badge> 项中尚未上传附件的问题。
                    <u class="fw-600">请在最终提交前再次确认。</u>
                    <br> 若您希望按当前状态提交，请点击下方的 <b>“{{ $t('최종 제출하기') }}”</b>。 -->
                    此确认窗口用于检查未勾选'回答完成'按钮的区域，或未附上必需证明文件的项目。请在最终提交前再次检查这些区域和项目。
                    如果您希望以当前状态提交，请点击下方的<b>“{{ $t('최종 제출하기') }}”</b>按钮。

                </div>
            </div>

            <b-card no-body>
                <b-tabs card :active-nav-item-class="['fw-500']">
                    <b-tab v-for="(category1, category1_index) in required_by_category1"
                        :key="`questions-${category1.category1_code}-require`"
                        :title="showTranslation(category1.category1_name, category1.category1_name_e, category1.category1_name_c)"
                        :active="category1_index === 0"
                        class="p-0"
                        :title-link-class="['gray-500']"
                        >
                        <b-table :items="category1.category3"
                            :fields="required_attachments_table_fields"
                            class="mb-0"
                            sticky-header hover
                            >

                            <template #cell(category3_name)="data">
                                {{ showTranslation(data.value, data.item.category3_name_e, data.item.category3_name_c) }}
                            </template>

                            <template #cell(category3_completed)="data">                                
                                <BIconCheckCircle :variant="data.value ? 'primary' : 'danger'" scale="1.4" class="mr-2" />
                                <span>
                                    {{ data.value ? $t('완료됨') : $t('답변중') }}
                                </span>
                            </template>
                            
                            <template #cell(category3_completed_file)="data">
                                <template v-if="data.value === ''">
                                    <span class="gray-500">{{ $t('첨부필수 문항 없음') }}</span>
                                </template>
                                <template v-else>
                                    <BIconCheckCircle scale="1.4" class="mr-2"
                                        :variant="data.item.category3_required_count === 0
                                            ? 'secondary'
                                            : data.value
                                                ? 'primary'
                                                : data.item.category3_submitted_files === 0
                                                    ? 'danger'
                                                    : 'info'
                                        "
                                        />
                                    <span :class="{ 'text-secondary': data.item.category3_required_count === 0 }">
                                        <template v-if="data.item.category3_required_count === 0">
                                            {{ $t('해당없음') }}
                                        </template>
                                        <template v-else>
                                            <template v-if="$i18n.locale === 'ko'">
                                                {{ data.item.category3_required_count }}개 중 {{ data.item.category3_submitted_files }}개 제출
                                            </template>
                                            <template v-else-if="$i18n.locale === 'en'">
                                                {{ data.item.category3_submitted_files }} of {{ data.item.category3_required_count }} were submitted.
                                            </template>
                                            <template v-else>
                                                已提交 {{ data.item.category3_required_count }} 个文件（共 {{ data.item.category3_submitted_files }} 个）。
                                            </template>
                                        </template>
                                    </span>
                                </template>
                            </template>

                            <template #cell(show_details)="row">
                                <b-button size="sm" class="mr-2" :disabled="row.item.category3_completed_file === null || row.item.category3_completed_file === ''"
                                    @click="row.toggleDetails"
                                    >
                                    {{ $t('상세내용') }} {{ row.detailsShowing ? $t('닫기') : $t('열기') }}
                                </b-button>
                            </template>


                            <template #row-details="row">
                                <b-card :header="`${showTranslation(row.item.category3_name, row.item.category3_name_e, row.item.category3_name_c)} - ${$t('상세내용')}`"
                                    no-body
                                    >
                                    <b-table :items="row.item.category3_required_files"
                                        :fields="required_attachments_subs_table_fields"
                                        class="mb-0"
                                        >

                                        <template #cell(sub_description)="data">
                                            <b-badge variant="dark" class="mr-1">{{ data.item?.label ?? data.item.sub_code }}</b-badge>
                                            {{ showTranslation(data.value, data.item.sub_description_ea, data.item.sub_description_ca) }}
                                        </template>

                                        <template #cell(is_fulfilled)="data">
                                            <template v-if="data.item.is_satisfied === false && data.value === ''">
                                                <span class="gray-500">{{ $t('해당없음') }}</span>
                                            </template>
                                            <template v-else>
                                                <BIconCheckCircle :variant="data.value ? 'primary' : 'danger'" scale="1.3" />
                                            </template>
                                        </template>
                                    </b-table>
                                </b-card>
                            </template>
                        </b-table>
                    </b-tab>
                </b-tabs>
            </b-card>

            <template #modal-footer>
                <div class="text-right">
                    <b-btn variant="primary" class="mr-2"
                        :disabled="is_submitting"
                        @click="$bvModal.show('submit-all')"
                        >
                        {{ $t('최종 제출하기') }}
                    </b-btn>
                    <b-btn variant="danger" :disabled="is_submitting"
                        @click="$refs['modal-required-attachments-list'].hide()"
                        >
                        {{ $t('닫기') }}
                    </b-btn>
                </div>
            </template>
        </b-modal>

        <!-- 미답변 문항 차단 모달 — 백엔드가 판정한 목록만 표시한다. 제출 버튼 없음. -->
        <b-modal ref="modal-unanswered-list" :title="$t('미답변 문항 확인 알림')"
            size="lg" centered scrollable
            ok-only :ok-title="$t('닫기')" ok-variant="danger"
            >
            <div class="text-center bg-white border border-gray f-105 rounded p-3 mb-3">
                {{ $t('아래 문항에 답변이 입력되지 않았습니다. 모든 문항에 답변을 입력한 뒤 다시 제출해 주세요.') }}
            </div>

            <b-list-group>
                <b-list-group-item v-for="question in unanswered_by_question"
                    :key="`unanswered-${question.question_code}`"
                    >
                    <div class="d-flex justify-content-between align-items-center mb-2">
                        <span class="fw-500">
                            {{ showTranslation(question.question_name, question.question_name_e, question.question_name_c) }}
                        </span>
                        <b-button size="sm" variant="blue" class="ml-2 text-nowrap"
                            @click="goToUnansweredQuestion(question.question_code)"
                            >
                            {{ $t('문항으로 이동') }}
                        </b-button>
                    </div>
                    <div v-for="sub in question.subs"
                        :key="`unanswered-${question.question_code}-${sub.sub_code}`"
                        class="f-95 mb-1"
                        >
                        <b-badge variant="dark" class="mr-1">{{ sub.label }}</b-badge>
                        <b-badge v-if="sub.sub_type_label_key" variant="light" class="mr-1">{{ $t(sub.sub_type_label_key) }}</b-badge>
                        <b-badge v-if="sub.reason_label_key" variant="warning" class="mr-1">{{ $t(sub.reason_label_key) }}</b-badge>
                        <b-badge v-for="(cell, cell_idx) in sub.missing_cells"
                            :key="`unanswered-cell-${sub.sub_code}-${cell_idx}`"
                            variant="danger" class="mr-1"
                            >
                            {{ $t('행') }} {{ cell.row }} · {{ cell.col }}
                        </b-badge>
                    </div>
                </b-list-group-item>
            </b-list-group>
        </b-modal>

        <b-modal id="submit-all" ref="submit-all" :title="$t('답변 최종 제출')"
            :no-close-on-esc="is_submitting"
            :no-close-on-backdrop="is_submitting"
            hide-footer
            centered
            >
            <b-card v-show="$i18n.locale === 'ko'">
                <div class="mb-2 indent-xl"> &#9312; 제출하신 답변은 평가 알고리즘 및 전문가 리뷰를 통해 평가됩니다. </div>
                <div class="indent-xl"> &#9313; 평가가 완료되면 <span class="fw-500 blue">이메일로 결과가 안내</span>되며, 본 시스템에서 결과를 확인하실 수 있습니다. </div>
            </b-card>
            <b-card v-show="$i18n.locale === 'ca'">
                <div class="mb-2 indent-xl"> &#9312; 您提交的答案将通过评估算法和专家评审进行评估。 </div>
                <div class="indent-xl"> &#9313;  评价结束后，您注册的邮箱将会收到一封评价完成的通知邮件，评价结果将显示在主页上。</div>
            </b-card>
            <b-card v-show="$i18n.locale === 'en'">
                <div class="mb-2 indent-xl"> &#9312; The answers you submitted will be evaluated through expert reviews.</div>
                <div class="indent-xl"> &#9313; When the evaluation process is do   ne, <span class="fw-500 blue">you will be notified through your email</span> as well as you can access the ESG report on the QESG Assessment.</div>
            </b-card>

            <div class="pt-5 text-center mb-5 fw-500">
                {{ $t(`제출 시 평가가 시작되며, 답변을 수정하실 수 없습니다.`) }} <br> {{ $t(`제출 하시겠습니까?`) }}
            </div>

            <div class="text-right py-2">
                <b-button variant="blue" :disabled="is_submitting"
                    @click="makeAllComplete"
                    >
                    <b-spinner v-if="is_submitting" class="white"></b-spinner>
                    {{ is_submitting ? '' : $t(`예, 제출하겠습니다.`) }}
                </b-button>
                <b-button variant="dark" class="ml-2"
                    :disabled="is_submitting"
                    @click="$bvModal.hide('submit-all')"
                    >
                    {{ $t(`취소`) }}
                </b-button>
            </div>
        </b-modal>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import * as th from 'tree-helper';

const {
    hasBlockUnansweredOption,
    groupUnansweredByQuestion,
    extractUnansweredItems,
    findQuestionNode
} = require('./unanswered-required-presentation.cjs');

export default {
    name: 'make-all-complete-button',
    props: {
        btn_size: {
            type: String,
            default: ''
        },
        btn_variant: {
            type: String,
            default: ''
        },
        disable_status: {
            type: Boolean,
            default: false
        }
    },
    data () {
        return { 
            is_submitting : false,
            is_request_required_files: false,
            required_by_category1: false,
            unanswered_by_question: [] // 미답변 차단 모달 목록 (문항별 묶음)
        };
    },
    computed:{
        ...mapState({
            user_id: state => state.user_id,
            campaign_state: state => state.campaign_in_progress.campaign_state,
            template_in_progress: state => state.template_in_progress,
            template_contents: state => [ ...state.template_in_progress.template_contents ],
            question_in_progress: state => state.question_in_progress
        }),
        project_code () {
            return this.$route.query.pc ?? this.$store.state.campaign_in_progress.project_code;
        },
        campaign_id () {
            return this.$route.query.cmp ?? this.$store.state.campaign_in_progress.campaign_id;
        },
        company_id () {
            return this.$route.query.cpn ?? this.$store.state.company_id;
        },
        progress_rate(){
            return this.$store.getters.getProgressRate;
        },
        // 캠페인 옵션 '미답변제출차단' On 여부 (판정은 백엔드, 프론트는 조회·표시만 한다)
        is_block_unanswered_enabled () {
            return hasBlockUnansweredOption(this.$store.state.campaign_in_progress.process_options);
        },
        check_disable () {
            if (this.disable_status === true || this.campaign_state !== '답변중') {
                return true;
            }
            
            return false;
        },
        required_attachments_table_fields () {
            return [
                { 
                    key: 'category3_name',
                    label: this.$t('분류'),
                    thClass: ['text-center', 'align-middle']
                },
                { 
                    key: 'category3_completed',
                    label: this.$t('답변 완료상태'),
                    thClass: ['text-center'],
                    tdClass: ['text-center', 'align-middle']
                },
                { 
                    key: 'category3_completed_file',
                    label: this.$t('첨부필수 완료상태'),
                    thClass: ['text-center'],
                    tdClass: ['text-center', 'align-middle']
                },
                { 
                    key: 'show_details',
                    label: this.$t('자세히 보기'),
                    thClass: ['text-center'],
                    tdClass: ['text-center', 'align-middle']
                }
            ];
        },
        required_attachments_subs_table_fields () {
            return [
                {
                    key: 'sub_description',
                    label: this.$t('문항번호 및 질문'),
                    thClass: ['text-center'],
                    tdClass: ['text-left', 'align-middle']
                },
                {
                    key: 'is_fulfilled',
                    label: this.$t('첨부상태'),
                    thClass: ['text-center'],
                    tdClass: ['text-center', 'align-middle']
                }
            ];
        },
        question_list () {
            let _question_list = [];
            this.template_contents.forEach(_question_cat1 => {
                _question_cat1.children.forEach(_question_cat2 => {
                    if (_question_cat2.children && _question_cat2.children.length) {
                        _question_cat2.children.forEach(_question_cat3 => {
                            _question_list.push({
                                category1_name: _question_cat1.question_name,
                                category1_name_e: _question_cat1.question_name_e,
                                category1_name_c: _question_cat1.question_name_c,
                                category1_code: _question_cat1.question_code,
                                category3_code: _question_cat3.question_code,
                                category3_name: _question_cat3.question_name,
                                category3_name_c: _question_cat3.question_name_c,
                                category3_name_e: _question_cat3.question_name_e,
                                question_contents: _question_cat3.question_contents && typeof _question_cat3.question_contents == 'string'
                                    ? JSON.parse(_question_cat3.question_contents)
                                    : [],
                                is_completed: _question_cat3.is_completed,
                            });
                        });
                    }
                });
            });

            return _question_list;
        }
    },
    methods: {
        async checkSubmitRequiredFiles() {
            try {
                this.is_request_required_files = true;

                // 미답변 차단 옵션 On 이면 첨부 안내보다 먼저 검사한다. 차단되면 여기서 흐름 종료.
                if (await this.checkUnansweredRequired()) {
                    return;
                }

                const _res_required_files = await this.$axios.get('/assessments/required/files', {
                    params: {
                        campaign_id: this.campaign_id,
                        company_id: this.company_id
                    }
                });
                
                const _required_files_list = _res_required_files.data.items;
                if (!_required_files_list.length) {
                    this.$bvModal.show('submit-all');
                } else {
                    let _combined_required = this.question_list.reduce((_combined_required_answer, _question) => {
                        const _required_file_subs = _required_files_list
                            .filter(_required_file => _required_file.question_code === _question.category3_code)
                            .map(({ sub_code,label, sub_description, sub_description_ca, sub_description_ea, is_fulfilled, is_satisfied, not_applicable }) => ({
                                sub_code,label, sub_description, sub_description_ca, sub_description_ea, is_fulfilled, is_satisfied,not_applicable
                            }));
                        _required_file_subs.sort((a,b) => {
                            const numA = parseFloat((a.label ?? a.sub_code).substring(1));
                            const numB = parseFloat((b.label ?? b.sub_code).substring(1));

                            return numA - numB;
                        });
                        _combined_required_answer.push({
                            ..._question,
                            is_completed: _question.is_completed === 'false' ? false : true,
                            is_completed_file: !_required_file_subs.length
                                ? null
                                : _required_file_subs
                                    .filter(({ is_fulfilled }) => is_fulfilled !== null)
                                    .every(({ is_fulfilled }) => is_fulfilled === true),
                            submitted_file_count: _required_file_subs.filter(({ is_fulfilled }) => is_fulfilled === true).length,
                            required_file_sub: _required_file_subs
                        });
                        return _combined_required_answer;
                    }, []);

                    _combined_required = _combined_required.reduce((_combined_required_by_cat1, _question) => {
                        const {
                            category1_code, category1_name, category1_name_e, category1_name_c,
                            category3_code, category3_name, category3_name_e, category3_name_c,
                            is_completed, is_completed_file, required_file_sub, submitted_file_count
                        } = _question;
                        
                        if (!_combined_required_by_cat1[category1_code]) {
                            _combined_required_by_cat1[category1_code] = {
                                category1_code, category1_name, category1_name_e, category1_name_c,
                                category3: []
                            };
                        }

                        _combined_required_by_cat1[category1_code]['category3'].push({
                            category3_code, category3_name, category3_name_e, category3_name_c,
                            category3_completed: is_completed,
                            category3_completed_file: is_completed_file,
                            category3_required_files: required_file_sub,
                            category3_required_count: required_file_sub.filter(({ is_fulfilled }) => is_fulfilled !== null).length,
                            category3_submitted_files: submitted_file_count,
                            show_details: true
                        });

                        return _combined_required_by_cat1;
                    }, {});

                    this.required_by_category1 = Object.values(_combined_required);
                    this.$nextTick(() => {
                        this.$refs['modal-required-attachments-list'].show();
                    });
                }
            } catch (error) {
                this.$refs['modal-required-attachments-list'].hide();
                alert(this.$t('죄송합니다. 최종 제출 처리중 장애가 발생했습니다. QESG info@qesg.co.kr로 문의해주시기 바랍니다.'));
            } finally {
                this.is_request_required_files = false;
            }
        },
        // 옵션 On 이면 백엔드에 미답변 목록을 묻는다. 목록이 있으면 차단 모달을 띄우고 true 를 돌려준다.
        // 엔드포인트 미배포(404)·네트워크 오류·enabled:false 는 기존 흐름으로 조용히 넘어간다 (degrade).
        async checkUnansweredRequired () {
            if (!this.is_block_unanswered_enabled) {
                return false;
            }

            let _items = [];
            try {
                const _res = await this.$axios.get('/assessments/required/answers', {
                    params: {
                        campaign_id: this.campaign_id,
                        company_id: this.company_id
                    }
                });
                if (_res.data?.enabled === false) {
                    return false;
                }
                _items = Array.isArray(_res.data?.items) ? _res.data.items : [];
            } catch (error) {
                // eslint-disable-next-line no-console
                console.warn('[MakeAllCompleteButton] /assessments/required/answers 조회 실패 — 기존 제출 흐름으로 진행', error?.response?.status ?? error?.message);
                return false;
            }

            if (!_items.length) {
                return false;
            }
            this.showUnansweredModal(_items);
            return true;
        },
        showUnansweredModal (items) {
            // 협력사 사이드바(template_contents) 순서가 진짜 순서 — question_list 가 같은 순회를 하므로 재사용
            const _sidebar_question_codes = this.question_list.map(({ category3_code }) => category3_code);
            this.unanswered_by_question = groupUnansweredByQuestion(items, _sidebar_question_codes);
            this.$nextTick(() => {
                this.$refs['modal-unanswered-list'].show();
            });
        },
        goToUnansweredQuestion (question_code) {
            this.$refs['modal-unanswered-list'].hide();
            const _node = findQuestionNode(this.template_contents, question_code);
            if (_node) {
                // 파이프라인 오버레이의 문항 이동과 같은 경로 — 사이드바 QuestionList.pageGo 가 받아 이동한다
                this.$EventBus.$emit('Questions-pipeline_overlay_question_clicked', _node);
            } else {
                this.$router.push({
                    name: 'Questions',
                    query: { ...this.$route.query },
                    params: { question_code }
                });
            }
        },
        async makeAllComplete () {
            let question_in_progresses = [];
            try {
                this.is_submitting = true;

                // 0) DFNiceServiceRequest의 상태를 in_progress => in_assessment로 저장함
                // await this.submitAndMakeInAssessment()

                //1) 현재 template_contents 에서 question_in_progress 객체를 복사해옴
                //2) question_code, question_contents(string 상태이므로 주의), question_infobox, question_name, 
                //   is_completed, visible, writable, comment
                
                //3) 여기에서 children length가 0일 경우, is_completed = 'true'로 원본을 만들고, 
                // let question_in_progresses = []
                th.breadthFirstSearch(this.template_contents, node => {
                    if(node.children && node.children.length == 0 && node.is_completed !== 'true'){
                        //node.is_completed = 'true'
                        let new_question_in_progress = {
                            question_code: node.question_code,
                            is_completed: 'true',
                            comment: node.comment
                        };
                        question_in_progresses.push(new_question_in_progress);
                    }
                });

                //4) DB에 저장한다
                //4-1) Question_in_progress 객체 저장
                //API: /save_question_answer (question_code, campaign_id, company_id, is_completed, comment)
                // const save_question_answer_path = this.$store.state.backend_host + '/save_question_answer'
                // let save_question_answer_promises = []

                // question_in_progresses.forEach(item => {
                //     let formData = new FormData()
                //     formData.append('question_code', item.question_code)
                //     formData.append('campaign_id', this.campaign_id)
                //     formData.append('company_id', this.company_id)
                //     formData.append('is_completed', item.is_completed)
                //     formData.append('comment', item.comment)

                //     save_question_answer_promises.push(
                //         this.$axios.post('/save_question_answer', formData)
                //     )
                // })

                // const results = await Promise.all(save_question_answer_promises)
                // results.forEach(response => {
                //     console.log(response)
                //     if (typeof response !== 'undefined') {
                //         th.breadthFirstSearch(this.template_contents, node => {
                //             if (node.question_code === response.data.question_code) {
                //                 // console.log(node.question_code)
                //                 node.is_completed = 'true'

                //                 let new_template_in_progress = { ...this.template_in_progress }
                //                 this.$store.commit('update_template_in_progress', new_template_in_progress)
                //                 // console.log(this.progress_rate)
                //             }
                //         })
                //     }
                // })

                let formData = new FormData();
                formData.append('campaign_id', this.campaign_id);
                formData.append('company_id', this.company_id);
                formData.append('is_completed', true);

                await this.$axios.post('/save_question_answer', formData);
                th.breadthFirstSearch(this.template_contents, node => {
                    node.is_completed = 'true';

                    let new_template_in_progress = { ...this.template_in_progress };
                    this.$store.commit('update_template_in_progress', new_template_in_progress);
                });

                this.question_in_progress.is_completed = 'true';
                let new_question_in_progress = { ...this.question_in_progress };
                this.$store.commit('update_question_in_progress', new_question_in_progress);
                await this.saveProgressLog(question_in_progresses);
                const progress_status_code = await this.submitAndMakeInAssessment();

                this.is_submitting = false;
                
                // this.$bvModal.hide('submit-all')
                this.$emit('refresh-tree');
                this.$emit('toast-success', this.$t('알림'), this.$t('전체 제출을 완료하였습니다.'));

                if (progress_status_code === 'in_assessment') {
                    this.$router.push({name: 'Main'});
                }
            } catch (error) {
                // 백엔드 제출 게이트(409 UNANSWERED_REQUIRED)는 장애가 아니라 차단 — 같은 차단 모달로 표시한다
                const _unanswered_items = extractUnansweredItems(error);
                if (_unanswered_items) {
                    this.showUnansweredModal(_unanswered_items);
                } else {
                    alert(this.$t('죄송합니다. 최종 제출 처리중 장애가 발생했습니다. QESG info@qesg.co.kr로 문의해주시기 바랍니다.'));
                }
            } finally {
                // 실패(409 차단 포함) 시에도 버튼 잠금을 풀어 답변 보완 후 재제출할 수 있게 한다
                this.is_submitting = false;
                // this.question_in_progress.is_completed = 'true'
                // let new_question_in_progress = { ...this.question_in_progress }
                // this.$store.commit('update_question_in_progress', new_question_in_progress)
                // await this.saveProgressLog(question_in_progresses)
                // this.is_submitting = false
                this.$bvModal.hide('submit-all');
                this.$refs['modal-required-attachments-list'].hide();
                // // console.log('이벤트 발신! refresh-tree')
                // this.$emit('refresh-tree')
                // this.$emit('toast-success', this.$t('알림'), this.$t('전체 제출을 완료하였습니다.'))
            }          
        },
        async submitAndMakeInAssessment () {
            const response = await this.$axios.put('/company/submit', {
                data: {
                    campaign_id: this.campaign_id,
                    company_id: this.company_id,
                    project_code: this.project_code
                }
            });

            return response.data.status_code;
        },
        async saveProgressLog (question_in_progresses) {
            //4-2) 로그 저장
            //API: /save_progress_log (user_id, campaign_id, company_id, question_code, is_completed, progress_rate = this.$store.getters.getProgressRate)
            
            // const save_progress_log_path = this.$store.state.backend_host + '/save_progress_log'

            let save_progress_log_promises = [];
            question_in_progresses.forEach(item => {
                let formData = new FormData();
                formData.append('user_id', this.user_id);
                formData.append('campaign_id', this.campaign_id);
                formData.append('company_id', this.company_id);
                formData.append('question_code', item.question_code);
                formData.append('is_completed', item.is_completed);
                formData.append('progress_rate', this.progress_rate);

                save_progress_log_promises.push(this.$axios.post('/save_progress_log', formData));
            });
            await Promise.all(save_progress_log_promises);
        },
        async informAuditors (mode) {
            // const path = this.$store.state.backend_host + '/inform_auditors'

            const formData = new FormData();
            formData.append('campaign_id', this.campaign_id);
            formData.append('company_id', this.company_id);
            formData.append('mode', mode); //'start_campaign', 'end_campaign'

            await this.$axios.post('/inform_auditors', formData);
        }
    }
};
</script>
